import React, { useEffect } from "react";
import { InputField } from "ui";
import usePostHprMobileVerify from "../../../apis/axon/hpr/use-post-hpr-mobile-verify";
import useHprHfrOnboardingStore from "../../../stores/hpr-hfr-dhis-onboarding";
import { OnboardingNavigationStates } from "../../../stores/hpr-hfr-dhis-onboarding/enums";
import VerifyFlowContainer from "../verify-flow-container";
import { track } from "../../../utils/tracking";
import { HFR_HPR_MIXPANEL_EVENTS } from "@elixir/types";
import { getMe } from "../../../utils/bridges/methods";

const VerifyPhoneNumber = () => {
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [docList, setDocList] = React.useState<Array<{ id: string, name: string }>>([]);


  const doctorId = useHprHfrOnboardingStore(
    (state) => state.doctorId
  );
  const setDoctorId = useHprHfrOnboardingStore(
    (state) => state.setDoctorId
  );

  useEffect(() => {
    const docs = getMe().doctors.map(doc => ({ id: doc.oid, name: doc.name }));
    setDocList(docs);
    if (docs.length > 0) {
      setDoctorId(docs[0].id);
    }
  }, [setDoctorId]);

  const transactionId = useHprHfrOnboardingStore(
    (state) => state.transactionId
  );
  const maskedMobileNumber = useHprHfrOnboardingStore(
    (state) => state.maskedMobileNumber
  );
  const setActiveScreen = useHprHfrOnboardingStore(
    (state) => state.setActiveScreen
  );
  const setSuggestedHprIds = useHprHfrOnboardingStore(
    (state) => state.setSuggestedHprIds
  );
  const setCreatedHprId = useHprHfrOnboardingStore(
    (state) => state.setCreatedHprId
  );

  useEffect(() => {
    track(HFR_HPR_MIXPANEL_EVENTS.AADHAAR_MOBILE_VERIFY_PAGE_VIEW);
  }, []);

  const { mutateAsync: postMobVerify, isLoading } = usePostHprMobileVerify();

  const submitPhoneNumber = async (phone: string) => {
    const last4Masked = maskedMobileNumber?.slice(-4);
    const last4Typed = phone.slice(-4);

    if (!doctorId) {
      setErrorMessage("Please select a doctor to proceed");
      return;
    }

    if (last4Masked === last4Typed && transactionId) {
      // call api
      const { success, data, error } = await postMobVerify({
        txn_id: transactionId,
        mobile_num: phone,
        doctor_oid: doctorId,
      });

      track(HFR_HPR_MIXPANEL_EVENTS.VERIFY_PHONE, {
        success: `${success}`,
        error: `${error?.actualMessage || error?.message}`,
        hpr_already_exists: `${!!data?.hpr?.hprId}`,
      });

      if (success && data) {
        const { verified, hpr, suggested_hpr_ids } = data;

        if (verified) {
          if (suggested_hpr_ids) {
            setSuggestedHprIds(suggested_hpr_ids);
          }

          if (hpr?.hprId) {
            setCreatedHprId(hpr.hprId);
          }

          setActiveScreen(
            hpr?.hprId
              ? OnboardingNavigationStates.HPR_ID_SUCCESSFUL
              : OnboardingNavigationStates.SETUP_HPR_ID
          );
          return;
        }
      } else {
        setErrorMessage(`Please try again`);
      }
      return;
    } else {
      setErrorMessage(
        `Number does not match with your Aadhaar number - ${maskedMobileNumber}`
      );
    }
  };

  return (
    <VerifyFlowContainer
      title="Verify yourself via Aadhar"
      primaryButtonText="Continue"
      primaryButtonClick={() => { }}
      primaryButtonLoading={isLoading}
    >
      <div className="dr-py-12 dr-space-y-4">
        {docList.length > 1 && <div className="">
          <div className="BodyRegular dr-text-darwin-neutral-700">
            Select doctor profile
          </div>
          <select
            value={doctorId}
            onChange={(e) => {
              setDoctorId(e.target.value);
            }}
            className="dr-mt-8 dr-w-full dr-border-1 dr-border-darwin-neutral-300 dr-rounded-8 dr-py-10 dr-px-12 dr-bg-darwin-neutral-0 BodyRegular dr-text-darwin-neutral-1000 focus:dr-outline-none focus:dr-border-darwin-primary-primary"
          >
            <option value="" disabled>
              Select Doctor
            </option>
            {docList.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.name}
              </option>
            ))}
          </select>
        </div>}
        <div className="BodyBold dr-text-darwin-touch-neutral-1000">
          Enter your phone number
        </div>
        <InputField
          value={phoneNumber}
          onChange={(e) => {
            setErrorMessage("");
            const value = e.target.value;
            if (value === "") {
              setPhoneNumber("");
              return;
            }
            if (!Number(value) || value.length > 10) return;
            setPhoneNumber(value);

            if (value.length === 10) {
              submitPhoneNumber(value);
            }
          }}
          prefixText="+91"
          helperText={`Use the phone number linked with your Aadhaar${maskedMobileNumber ? `- ${maskedMobileNumber}` : ""
            }`}
          error={errorMessage}
          autofocus={true}
        />
      </div>
    </VerifyFlowContainer>
  );
};

export default VerifyPhoneNumber;
